import axios from 'axios';
import router from '@/router';

/**
 * Shared axios instance with:
 * 1. Global 401 response interceptor (session expired handler).
 * 2. In-flight request deduplication and TTL-based response caching:
 *    - `cachedGet(url, params, ttl)`
 *    - `cachedPost(url, data, ttl)`
 *    - `cachedRequest(method, url, payload, ttl)`
 *    - `clearCache(pattern)`
 *    - Direct `axios.cachedGet` / `axios.cachedPost` methods
 *    - Optional `config.cache` support for standard axios calls
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

const cache = {};
const inflight = {};

export function makeKey(method, url, data) {
  const base = `${(method || 'get').toLowerCase()}:${url}`;
  if (data && Object.keys(data).length) {
    try {
      return `${base}:${JSON.stringify(data)}`;
    } catch {
      return `${base}:${String(data)}`;
    }
  }
  return base;
}

export function cachedGet(url, params = {}, ttl = DEFAULT_TTL) {
  return cachedRequest('get', url, params, ttl);
}

export function cachedPost(url, data = {}, ttl = DEFAULT_TTL) {
  return cachedRequest('post', url, data, ttl);
}

export function cachedRequest(method, url, payload, ttl = DEFAULT_TTL) {
  const key = makeKey(method, url, payload);
  const now = Date.now();

  if (cache[key] && now - cache[key].timestamp < ttl) {
    return Promise.resolve(cache[key].data);
  }

  if (inflight[key]) return inflight[key];

  const request = method.toLowerCase() === 'get'
    ? axios.get(url, { params: payload })
    : axios.post(url, payload);

  inflight[key] = request
    .then((res) => {
      cache[key] = { data: res.data, timestamp: Date.now() };
      return res.data;
    })
    .catch((err) => {
      throw err;
    })
    .finally(() => {
      delete inflight[key];
    });

  return inflight[key];
}

export function clearCache(pattern) {
  if (!pattern) {
    Object.keys(cache).forEach((k) => delete cache[k]);
    return;
  }
  Object.keys(cache).forEach((k) => {
    if (k.includes(pattern)) delete cache[k];
  });
}

// Attach helpers directly to the axios instance
axios.cachedGet = cachedGet;
axios.cachedPost = cachedPost;
axios.cachedRequest = cachedRequest;
axios.clearCache = clearCache;

// Support config.cache (boolean or { ttl: number }) for standard axios calls
const defaultAdapter = axios.getAdapter ? axios.getAdapter(axios.defaults.adapter) : axios.defaults.adapter;
if (typeof defaultAdapter === 'function') {
  axios.defaults.adapter = async function cachedAdapter(config) {
    if (config && config.cache) {
      const ttl = typeof config.cache === 'object' && config.cache.ttl ? config.cache.ttl : DEFAULT_TTL;
      const method = (config.method || 'get').toLowerCase();
      const payload = method === 'get' ? config.params : config.data;
      const key = makeKey(method, config.url, payload);
      const now = Date.now();

      if (cache[key] && now - cache[key].timestamp < ttl) {
        return Promise.resolve({
          data: cache[key].data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: {},
        });
      }

      if (inflight[key]) {
        const data = await inflight[key];
        return {
          data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: {},
        };
      }

      const requestPromise = defaultAdapter(config)
        .then((response) => {
          cache[key] = { data: response.data, timestamp: Date.now() };
          return response.data;
        })
        .finally(() => {
          delete inflight[key];
        });

      inflight[key] = requestPromise;

      const data = await requestPromise;
      return {
        data,
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
        request: {},
      };
    }

    return defaultAdapter(config);
  };
}

// Guard flag — prevents multiple concurrent 401s from each triggering
// a redirect / localStorage.clear() race.
let isLoggingOut = false;

axios.interceptors.response.use(
  // Successful responses pass through untouched.
  (response) => response,

  // Rejected responses — check for 401.
  (error) => {
    if (error.response && error.response.status === 401 && !isLoggingOut) {
      isLoggingOut = true;

      // Wipe all persisted auth data.
      localStorage.clear();

      // Redirect to login with an explanatory query param.
      router.push({ name: 'login', query: { session: 'expired' } }).finally(() => {
        isLoggingOut = false;
      });
    }

    return Promise.reject(error);
  }
);

export { DEFAULT_TTL };
export default axios;
